import {
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';

import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import SaveIcon from '@mui/icons-material/Save';

import PricingFilters from './PricingTable/PricingFilters';
import Saving from './PricingTable/Saving';

import useUserContext from '../providers/User/UserProvider';

import RowsProvider from '../providers/Rows/RowsProvider';
import PricingProvider from '../providers/Pricing/PricingProvider';
import BackgroundProvider from '../providers/Background/BackgroundProvider';
import NotificationProvider from '../providers/Notification/NotificationProvider';

import CommentPresenter from './Common/CommentPresenter';
import NoInternetConnectionAlert from './Common/NoInternetConnectionAlert';

import {
  AgGridColumnView,
  CustomerType,
  Nullable,
  PdfQuality,
  PricingBasis,
  PricingSheet,
  PricingSheetRow,
  PricingSheetState,
  RoundingBasis,
  SelectSheetOption,
  TableType,
} from '../../../shared/types';

import { WARNING_THRESHOLD } from '../utils/pricingFormulas';

import { formatPricingSheetItemsPost } from '../helpers/postSheetRows';
import getUnderWarningPriceCount from '../helpers/getUnderWarningPriceCount';
import getUnderCoefficientCount from '../helpers/getUnderCoefficientCount';

import useOnlineStatus from '../hooks/useOnlineStatus';
import useRowsHistory from '../hooks/useRowsHistory';

import { usePreventWindowUnload } from '../utils/commonUtils';

import { updatePricingSheetById } from '../services/pricingSheets';
import {
  Alert,
  AlertTitle,
  CircularProgress,
  Grid,
  SelectChangeEvent,
} from '@mui/material';
import EditPricesDialog from './PricingTable/EditPricesDialog';
import EditViewDialog from './PricingTable/EditViewDialog';
import RemoveItemsDialog from './PricingTable/RemoveItemsDialog';
import SelectPricingColumns from './PricingTable/SelectPricingColumns';
import EditPricingBasis from './PricingTable/EditPricingBasis';
import EditRoundingBases from './PricingTable/EditRoundingBases';

import { ResetFiltersButton } from './PricingTable/ResetFiltersButton';

import SearchExternalSheet, {
  ExternalPricingSheet,
} from './PricingTable/SearchExternalSheet';
import CopyPricesDialog, {
  CopyColumns,
} from './PricingTable/CopyPricesDialog';
import { downloadPdf } from '../helpers/downloadPdf';

import History from './History/History';
import { isHistoryChangedInPricingSide } from './History/changeChecks';
import { useFilters } from '../hooks/useFilters';
import AgGrid from './PricingTable/AgGrid';

import { useColumns } from '../hooks/useColumns';
import WarningCounts from './Cells/common/WarningCounts';
import { getGroupFilterForGroup } from '../helpers/groupFilter';
import CoefficientChips from './Cells/common/CoefficientChips';
import CommentToolTip from './Tooltips/CommentToolTip';
import CommentIndicator from './Common/CommentIndicator';
import calculateMeansByGroup from '../utils/calculateMeansByGroup';
import { clone } from '../utils/genericUtils';
import {
  ExternalPricingSheetItem,
  PricingSheetItemResponse,
} from '../../../shared/types/pricingSheetItems';
import putSheetRows from '../helpers/putSheetRows';
import { RentalHistory } from './RentalHistory/RentalHistory';
import { useApi } from '../hooks/useApi';
import { updateRentalHistoryPricingSheet } from '../services/rentalHistory';
import { RentalHistorySourceType } from '../../../shared/types/pricingSheets';
import { containsRentalHistoryRows } from '../../../shared/helpers/containsRentalHistoryRows';
import { addDays, endOfDay, startOfDay, subYears } from 'date-fns';
import _ from 'lodash';
import { useRentalHistoryJob } from '../hooks/useRentalHistoryJob';
import { countEstimateYieldMonth } from '../../../shared/countEstimateYieldMonth';
import { countEstimateYieldDay } from '../../../shared/countEstimateYieldDay';
import { useRentalHistory } from '../hooks/useRentalHistory';

const formatPricingSheetRows = (
  pricingSheetRows: PricingSheetRow[],
  rentalHistory: any,
) => {
  return pricingSheetRows.map((row: PricingSheetRow) => {
    const rentalDaysDay = _.get(
      rentalHistory,
      ['data', row.catClass, 'rentalDaysDay'],
      undefined,
    );
    const rentalDaysMonth = _.get(
      rentalHistory,
      ['data', row.catClass, 'rentalDaysMonth'],
      undefined,
    );
    const rentalProfitDay = _.get(
      rentalHistory,
      ['data', row.catClass, 'totalPriceDay'],
      undefined,
    );
    const rentalProfitMonth = _.get(
      rentalHistory,
      ['data', row.catClass, 'totalPriceMonth'],
      undefined,
    );

    return {
      ...row,
      rentalDaysDay: rentalDaysDay,
      rentalDaysMonth: rentalDaysMonth,
      estimatedYieldDay: Number(
        (row?.dayPrice ?? 0) * (rentalDaysDay ?? 0),
      ),
      estimatedYieldMonth: Number(
        ((row?.monthPrice ?? 0) / 30) * (rentalDaysMonth ?? 0),
      ),
      historicYieldDay: rentalHistory.totalDayPrice,
      historicYieldMonth: rentalHistory.totalMonthPrice,
      rentalProfitDay,
      rentalProfitMonth,
    };
  });
};

interface PricingProps {
  currentPricingSheetRows: PricingSheetRow[];
  setCurrentPricingSheetRows: React.Dispatch<
    React.SetStateAction<[] | PricingSheetRow[]>
  >;
}

const getInitialColumns = (pricingBasis: PricingBasis) => {
  if (pricingBasis === 'DAY') {
    return AgGridColumnView.PRICING_DAY;
  }
  return AgGridColumnView.PRICING_BOTH;
};

export type TableFilter = (row: PricingSheetRow) => boolean;

const Pricing: React.FC<PricingProps> = ({
  currentPricingSheetRows,
  setCurrentPricingSheetRows,
}) => {
  const theme = useTheme();

  const { setHasUnsavedChanges } = useUserContext();
  const {
    pricingSheetRows,
    selectedRows,
    setSelectedRows,
    preSelectedRows,
    updateRow,
    warningCounts,
    dispatchPricingSheetRows,
  } = useContext(RowsProvider);

  const {
    dayWarnings,
    dayLightWarnings,
    dayBelow70Warnings,
    dayZeroPriceWarnings,
    monthWarnings,
    monthLightWarnings,
    monthBelow70Warnings,
    monthZeroPriceWarnings,
    invalidPriceThresholdRows,
  } = warningCounts;

  const {
    pricingSheetId,
    setItemFilterValue,
    externalPricingSheetId,
    isMiraSheet,
    miraSuccess,
    miraSheetId,
    comparisonPricingSheets,
    setExternalPricingSheetId,
    comparisonSheetName,
    setComparisonSheetName,
    externalSheetSelectedOption,
    setExternalSheetSelectedOption,
    rentalHistoryProvider,
    updateRentalHistoryProvider,
    miraSheetIdExternal,
    setMiraSheetIdExternal,
    comparisonSheetRows,
    setComparisonSheetRows,
  } = useContext(PricingProvider);

  const { backgroundInfo, setBackgroundInfo } = useContext(
    BackgroundProvider,
  );
  const {
    pricingBasis,
    roundingBasisSheet,
    customerId,
    customerName,
    companyId,
    customerType,
    priceGroupId,
    isRenew,
    renewVersion,
    name,
  } = backgroundInfo;

  const previousVersionName = (name: string) => {
    const nameSplits = name.split('_');
    const nameWithoutRenewInfo = nameSplits.slice(
      0,
      nameSplits.length - 1,
    );

    if (renewVersion === 1) {
      return nameWithoutRenewInfo.join('_');
    }
    return nameWithoutRenewInfo
      .concat([`U${renewVersion - 1}`])
      .join('_');
  };

  const { setNotification } = useContext(NotificationProvider);
  const {
    activeFilters,
    addFilter,
    toggleActiveFilter,
    isChecked,
    clearFilters,
    applyActiveFilters,
  } = useFilters();

  const isEditingDisabled = isRenew;

  const {
    columnDefs,
    groupingStyle,
    gridColumnView,
    groupingValues,
    setDefaultGroupingValues,
    updateGridColumnView,
    updateGroupingValues,
    getMainMenuItems,
  } = useColumns(getInitialColumns(pricingBasis), {
    roundingBasisSheet,
    handleUpdateRowValue: updateRow,
    isEditingDisabled,
  });

  // Initial fetch of rental history for customer
  const {
    data: rentalHistory = {},
    loading: isLoadingRentalHistory,
    isSuccess,
  } = useRentalHistoryJob(
    customerId,
    startOfDay(addDays(subYears(new Date(), 1), 1)),
    endOfDay(startOfDay(new Date())),
    customerType !== CustomerType.COMPANY_CHAIN &&
      pricingSheetRows.length > 0 &&
      !containsRentalHistoryRows(pricingSheetRows),
  );

  // Initial fetch of rental history for company chain
  const {
    data: rentalHistoryCompanyChain = {},
    isFetching: isLoadingRentalHistoryCompanyChain,
    isSuccess: isSuccessCompanyChain,
  } = useRentalHistory(
    RentalHistorySourceType.CompanyChain,

    companyId,
    startOfDay(addDays(subYears(new Date(), 1), 1)),
    endOfDay(startOfDay(new Date())),
    customerType === CustomerType.COMPANY_CHAIN &&
      pricingSheetRows.length > 0 &&
      !containsRentalHistoryRows(pricingSheetRows),
  );

  const isOnline = useOnlineStatus();

  const [
    rowsHistory,
    currentRowsInHistory,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    currentItemIndustryPercentsInHistory,
    setRowsHistory,
    undo,
    undoToIndex,
    redo,
    redoToIndex,
    currentIndex,
    setCurrentIndex,
  ] = useRowsHistory({
    keys: ['comparisonDayPrice', 'comparisonMonthPrice'],
    values: comparisonSheetRows,
  });

  const [companyChains] = useApi('/clients/mira/company-chains');
  const agGridRef = useRef<any>();

  const [savingState, setSavingState] = useState<boolean>(false);
  const [savingTimeStamp, setSavingTimeStamp] = useState<Date>();
  const [error, setError] = useState<boolean>(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const preSelectedPricingSheetRows = preSelectedRows;

  /**
   *  HANDLERS
   */

  const updateTimeStampToNewestChange = () => {
    setSavingTimeStamp(
      new Date(rowsHistory[rowsHistory.length - 1]?.timeStamp),
    );
  };

  const getPricingSheetPdf = async (
    id: string,
    name: string,
    quality: PdfQuality,
  ) => {
    try {
      setPdfLoading(true);
      await downloadPdf(id, name, quality);
    } catch (error) {
      setNotification({
        type: 'SNACKBAR',
        duration: 4000,
        severity: 'error',
        message: 'Hinnaston lataaminen epäonnistui',
      });
    } finally {
      setPdfLoading(false);
    }
  };

  const updatePricingSheetState = async (
    state: PricingSheetState,
    pricingBasis: PricingBasis,
  ) => {
    try {
      await updatePricingSheetById(pricingSheetId, {
        state: state,
        pricingBasis: pricingBasis,
      });
      setNotification({
        type: 'SNACKBAR',
        duration: 4000,
        severity: 'success',
        message: 'Hinnaston tila päivitetty palvelimelle!',
      });
    } catch (error) {
      console.error(error);
      setNotification({
        type: 'SNACKBAR',
        duration: 4000,
        severity: 'error',
        message: 'Hinnaston tilan päivitys palvelimelle epäonnistui!',
      });
    }
  };

  const handleRentalHistoryCustomerChange = useCallback(
    async (
      rentalHistoryCustomerName: string,
      rentalHistoryCustomerId: string,
      rentalHistoryStartDate: Date,
      rentalHistoryEndDate: Date,
      rentalHistoryCompanyChainId: string,
      rentalHistorySourceType: RentalHistorySourceType,
      pricingSheetItems: PricingSheetRow[],
      rentalHistoryCompanyName: string,
    ) => {
      try {
        await updateRentalHistoryPricingSheet(
          pricingSheetId,
          {
            rentalHistoryCustomerId,
            rentalHistoryCustomerName,
            rentalHistoryStartDate,
            rentalHistoryEndDate,
            rentalHistoryCompanyChainId,
            rentalHistorySourceType,
            rentalHistoryCompanyName,
          },
          formatPricingSheetItemsPost(
            pricingSheetId,
            pricingSheetItems.filter((item) => item.preSelected),
          ),
        );
        updateRentalHistoryProvider(
          'startDate',
          rentalHistoryStartDate,
        );
        updateRentalHistoryProvider('endDate', rentalHistoryEndDate);
        updateRentalHistoryProvider(
          'sourceType',
          rentalHistorySourceType,
        );
        if (
          rentalHistorySourceType === RentalHistorySourceType.Customer
        ) {
          // Set customer data
          updateRentalHistoryProvider(
            'customer',
            rentalHistoryCustomerName,
          );
          updateRentalHistoryProvider(
            'customerId',
            rentalHistoryCustomerId,
          );
          // Reset company date
          updateRentalHistoryProvider('companyName', '');
          updateRentalHistoryProvider('companyId', '');
        } else if (
          rentalHistorySourceType ===
          RentalHistorySourceType.CompanyChain
        ) {
          // Set company data
          updateRentalHistoryProvider(
            'companyName',
            rentalHistoryCompanyName,
          );
          updateRentalHistoryProvider(
            'companyId',
            rentalHistoryCompanyChainId,
          );
          // Reset customer data
          updateRentalHistoryProvider('customer', '');
          updateRentalHistoryProvider('customerId', '');
        }

        dispatchPricingSheetRows({
          type: 'updatePricingSheetRows',
          newRows: pricingSheetItems,
        });
      } catch (error) {
        console.error(error);
      }
    },
    [
      dispatchPricingSheetRows,
      pricingSheetId,
      updateRentalHistoryProvider,
    ],
  );

  const handleColumnViewChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    updateGridColumnView(event.target.value as AgGridColumnView);
  };

  const handlePricingBasisChange = (
    event: SelectChangeEvent<PricingBasis>,
  ) => {
    if (event.target.value === 'DAY') {
      updateGridColumnView(AgGridColumnView.PRICING_DAY);
      setBackgroundInfo({
        ...backgroundInfo,
        pricingBasis: 'DAY',
      });
    } else {
      updateGridColumnView(AgGridColumnView.PRICING_BOTH);
      setBackgroundInfo({
        ...backgroundInfo,
        pricingBasis: 'BOTH',
      });
    }
    clearFilters();
    updatePricingSheetState(
      backgroundInfo.state,
      event.target.value as PricingBasis,
    );
  };

  const handleComparisonSheetChange = (
    newValue: SelectSheetOption,
  ) => {
    const comparisonSheet = comparisonPricingSheets.find(
      (sheet: PricingSheet) => Number(sheet.id) === newValue.id,
    );
    setExternalSheetSelectedOption(newValue);
    setComparisonSheetName(comparisonSheet?.name);
  };

  const handlePricingSheetItemsResponse = (
    pricingSheetItems: PricingSheetItemResponse[],
  ) => {
    const copy = clone<PricingSheetRow[]>(pricingSheetRows);
    for (const pricingSheetRow of copy) {
      if (pricingSheetRow) {
        const item = pricingSheetItems.find(
          (row: { item: PricingSheetRow }) =>
            pricingSheetRow?.catClass === row.item?.catClass,
        );

        pricingSheetRow.comparisonDayPrice = item?.dayPrice
          ? Number(item.dayPrice)
          : undefined;

        pricingSheetRow.comparisonMonthPrice = item?.monthPrice
          ? Number(item.monthPrice)
          : undefined;
      }
    }

    setComparisonSheetRows(
      copy.reduce<any>((acc, row) => {
        acc[row.catClass] = {
          comparisonDayPrice: row.comparisonDayPrice,
          comparisonMonthPrice: row.comparisonMonthPrice,
        };
        return acc;
      }, {}),
    );

    dispatchPricingSheetRows({
      type: 'updatePricingSheetRows',
      newRows: copy,
    });
  };

  const handleExternalItemsResponse = (
    externalPricingSheetItems: ExternalPricingSheetItem[],
  ) => {
    const copy = clone<PricingSheetRow[]>(pricingSheetRows);
    for (const pricingSheetRow of copy) {
      if (pricingSheetRow) {
        const item = externalPricingSheetItems.find(
          (row) => pricingSheetRow?.catClass === row.item?.catClass,
        );

        // if sheet is mira sheet we have to multiply prices by 10000
        pricingSheetRow.comparisonDayPrice = item?.dayPrice
          ? Number(item.dayPrice) * 10000
          : undefined;

        pricingSheetRow.comparisonMonthPrice = item?.monthPrice
          ? Number(item.monthPrice) * 10000
          : undefined;
      }
    }

    setComparisonSheetRows(
      copy.reduce<any>((acc, row) => {
        acc[row.catClass] = {
          comparisonDayPrice: row.comparisonDayPrice,
          comparisonMonthPrice: row.comparisonMonthPrice,
        };
        return acc;
      }, {}),
    );

    dispatchPricingSheetRows({
      type: 'updatePricingSheetRows',
      newRows: copy,
    });
  };

  const handleComparisonSheetResponse = (
    externalPricingSheet: ExternalPricingSheet,
  ) => {
    setComparisonSheetName(externalPricingSheet.sheetName);
  };

  const handlePriceChanges = (newRows: PricingSheetRow[]) => {
    const copy = clone<PricingSheetRow[]>(pricingSheetRows);
    for (const pricingSheetRow of copy) {
      if (
        pricingSheetRow?.preSelected &&
        newRows.some(
          (row: PricingSheetRow) =>
            row.catClass === pricingSheetRow.catClass,
        )
      ) {
        const matchingNewRow = newRows.find(
          (row: PricingSheetRow) =>
            row.catClass === pricingSheetRow.catClass,
        );

        if (matchingNewRow) {
          Object.assign(pricingSheetRow, matchingNewRow);
        }
      }
    }

    dispatchPricingSheetRows({
      type: 'updatePricingSheetRows',
      newRows: copy,
    });
    const preSelectedNewRows = copy.filter(
      (row: PricingSheetRow) => row.preSelected,
    );

    saveRows([...preSelectedNewRows]);
    setCurrentPricingSheetRows([...preSelectedNewRows]);
    setSelectedRows([]); //provider
    agGridRef.current?.clearAllSelections(); // grid
  };

  const handleCopyPricesComparison = (
    selectedRows: PricingSheetRow[],
    copyColumns: CopyColumns,
  ) => {
    const copy = clone<PricingSheetRow[]>(pricingSheetRows);
    for (const pricingSheetRow of copy) {
      if (
        pricingSheetRow?.preSelected &&
        selectedRows.some(
          (row: PricingSheetRow) =>
            row.catClass === pricingSheetRow.catClass,
        )
      ) {
        const dayPrice = pricingSheetRow?.comparisonDayPrice;
        const monthPrice = pricingSheetRow?.comparisonMonthPrice;

        if (
          dayPrice &&
          (copyColumns === 'DAY' || copyColumns === 'BOTH')
        ) {
          pricingSheetRow.dayPrice = Number(dayPrice);
          if (pricingSheetRow.rentalDaysDay) {
            pricingSheetRow.estimatedYieldDay = countEstimateYieldDay(
              Number(dayPrice),
              pricingSheetRow.rentalDaysDay,
            );
          }
        }
        if (
          monthPrice &&
          (copyColumns === 'MONTH' || copyColumns === 'BOTH')
        ) {
          pricingSheetRow.monthPrice = Number(monthPrice);

          if (pricingSheetRow.rentalDaysMonth) {
            pricingSheetRow.estimatedYieldMonth =
              countEstimateYieldMonth(
                Number(monthPrice),
                pricingSheetRow.rentalDaysMonth,
              );
          }
        }
        pricingSheetRow.selected = false;
      }
    }

    dispatchPricingSheetRows({
      type: 'updatePricingSheetRows',
      newRows: copy,
    });
    const preSelectedNewRows = copy.filter(
      (row: PricingSheetRow) => row.preSelected,
    );

    saveRows([...preSelectedNewRows]);
    setCurrentPricingSheetRows([...preSelectedNewRows]);
    setSelectedRows([]); // provider
    agGridRef.current?.clearAllSelections(); // grid
  };

  const handleCopyPricesProposal = (
    selectedRows: PricingSheetRow[],
    copyColumns: CopyColumns,
  ) => {
    const copy = clone<PricingSheetRow[]>(pricingSheetRows);

    for (const copyRow of copy) {
      if (
        copyRow.preSelected &&
        selectedRows.some(
          (row: PricingSheetRow) => row.catClass === copyRow.catClass,
        )
      ) {
        const { proposalDayPrice, proposalMonthPrice } = copyRow;

        if (
          proposalDayPrice &&
          (copyColumns === 'DAY' || copyColumns === 'BOTH')
        ) {
          copyRow.dayPrice = proposalDayPrice;

          if (copyRow.rentalDaysDay) {
            copyRow.estimatedYieldDay = countEstimateYieldDay(
              proposalDayPrice,
              copyRow.rentalDaysDay,
            );
          }
        }
        if (
          proposalMonthPrice &&
          (copyColumns === 'MONTH' || copyColumns === 'BOTH')
        ) {
          copyRow.monthPrice = proposalMonthPrice;

          if (copyRow.rentalDaysMonth) {
            copyRow.estimatedYieldMonth = countEstimateYieldMonth(
              proposalMonthPrice,
              copyRow.rentalDaysMonth,
            );
          }
        }
        copyRow.selected = false;
      }
    }

    dispatchPricingSheetRows({
      type: 'updatePricingSheetRows',
      newRows: copy,
    });

    const preSelectedNewRows = copy.filter(
      (row: PricingSheetRow) => row.preSelected,
    );

    saveRows([...preSelectedNewRows]);
    setCurrentPricingSheetRows([...preSelectedNewRows]);
    setSelectedRows([]); // provider
    agGridRef.current?.clearAllSelections(); // grid
  };

  // Set initial rental history data for customer
  useEffect(() => {
    if (
      isSuccess &&
      rentalHistory &&
      !containsRentalHistoryRows(pricingSheetRows)
    ) {
      handleRentalHistoryCustomerChange(
        customerName,
        customerId,
        addDays(subYears(new Date(), 1), 1),
        endOfDay(startOfDay(new Date())),
        '',
        RentalHistorySourceType.Customer,
        formatPricingSheetRows(pricingSheetRows, rentalHistory),
        '',
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, rentalHistory]);

  // Set initial rental history data for company chain
  useEffect(() => {
    if (
      isSuccessCompanyChain &&
      rentalHistoryCompanyChain &&
      !containsRentalHistoryRows(pricingSheetRows)
    ) {
      handleRentalHistoryCustomerChange(
        '',
        '',
        addDays(subYears(new Date(), 1), 1),
        endOfDay(startOfDay(new Date())),
        companyId,
        RentalHistorySourceType.CompanyChain,
        formatPricingSheetRows(
          pricingSheetRows,
          rentalHistoryCompanyChain,
        ),
        customerName,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessCompanyChain, rentalHistoryCompanyChain]);

  const updateSheetStateLocally = () => {
    try {
      const underWarningPriceCount = getUnderWarningPriceCount(
        preSelectedPricingSheetRows,
      );
      const underCoefficientCount = getUnderCoefficientCount(
        preSelectedPricingSheetRows,
      );

      if (
        underWarningPriceCount > 0 ||
        underCoefficientCount > WARNING_THRESHOLD
      ) {
        setBackgroundInfo({
          ...backgroundInfo,
          state: PricingSheetState.WarningDraft,
        });
      } else {
        setBackgroundInfo({
          ...backgroundInfo,
          state: PricingSheetState.OkDraft,
        });
      }
    } catch (error) {
      setNotification({
        severity: 'error',
        message: 'Hinnaston tilaa päivittäessä tapahtui virhe!',
      });
    }
  };

  const saveRowsState = async () => {
    if (pricingSheetRows.length > 0 && shouldSaveRowsState) {
      setRowsHistory([
        ...rowsHistory,
        {
          timeStamp: Date.now(),
          rows: pricingSheetRows,
          roundingBasisSheet: backgroundInfo.roundingBasisSheet,
          roundingBasisPdf: backgroundInfo.roundingBasisPdf,
        },
      ]);
      setCurrentIndex(rowsHistory.length);
    }
  };

  const handleClearComparisonPrices = () => {
    const copy = clone<PricingSheetRow[]>(pricingSheetRows);
    for (const pricingSheetRow of copy) {
      if (pricingSheetRow) {
        pricingSheetRow.comparisonDayPrice = undefined;
        pricingSheetRow.comparisonMonthPrice = undefined;
      }
    }
    setComparisonSheetRows(undefined);
    dispatchPricingSheetRows({
      type: 'updatePricingSheetRows',
      newRows: copy,
    });
    setComparisonSheetName('');
  };

  const save = async (
    rows: PricingSheetRow[],
    moveInHistory: boolean = false,
  ) => {
    if (!moveInHistory) {
      await saveRows([...rows]);
    }
  };

  const onSaveClick = () => {
    updatePricingSheetState(
      backgroundInfo.state,
      backgroundInfo.pricingBasis,
    );
    save(preSelectedPricingSheetRows, false);
  };

  const saveRows = async (
    rows: PricingSheetRow[],
    roundingBasisSheet?: RoundingBasis,
  ) => {
    let successfully = false;
    setError(false);
    setSavingState(true);
    try {
      const response = await putSheetRows(pricingSheetId, rows);
      if (response?.status === 204) {
        setCurrentPricingSheetRows([...rows]);

        setSavingTimeStamp(
          new Date(rowsHistory[currentIndex]?.timeStamp),
        );
        setSavingState(false);

        successfully = true;
      }
    } catch (error) {
      setNotification({
        severity: 'error',
        message: 'Hinnastoa tallentaessa tapahtui virhe!',
      });
      setError(true);
    } finally {
      setSavingState(false);
      return successfully;
    }
  };

  const onClickDownloadDraftPdf = async () => {
    try {
      await saveRows(preSelectedPricingSheetRows);
      updatePricingSheetState(
        backgroundInfo.state,
        backgroundInfo.pricingBasis,
      );

      getPricingSheetPdf(pricingSheetId, 'draft', PdfQuality.LOW);
    } catch (error) {
      setNotification({
        severity: 'error',
        message: 'Hinnastoa ladatessa tapahtui virhe!',
      });
      setError(true);
    }
  };

  const removeRows = () => {
    selectedRows.forEach((row: PricingSheetRow) => {
      dispatchPricingSheetRows({
        id: row.catClass,
        type: 'updateSelectedStatuses',
        selectedStatus: true,
      });
    });
    selectedRows.forEach((row: PricingSheetRow) => {
      dispatchPricingSheetRows({
        id: row.catClass,
        type: 'updatePreSelectedStatuses',
        selectedStatus: true,
      });
    });

    setNotification({
      type: 'SNACKBAR',
      severity: 'success',
      message: `Poistettu ${selectedRows.length} tuotetta hinnoittelusta`,
      duration: 3000,
    });
  };

  /**
   * HELPERS
   */

  const isUnsavedChanges = !(
    JSON.stringify(
      currentPricingSheetRows.map((row) => {
        return {
          id: row.id,
          catClass: row.catClass,
          dayPrice: row.dayPrice,
          monthPrice: row.monthPrice,
        };
      }),
    ) ===
    JSON.stringify(
      preSelectedPricingSheetRows.map((row: PricingSheetRow) => {
        return {
          id: row.id,
          catClass: row.catClass,
          dayPrice: row.dayPrice,
          monthPrice: row.monthPrice,
        };
      }),
    )
  );

  const isStateAndSaveSame =
    currentIndex === 0 && !savingTimeStamp
      ? true
      : rowsHistory.length > 1
        ? savingTimeStamp?.getTime() ===
          new Date(rowsHistory[currentIndex]?.timeStamp).getTime()
        : true;

  const shouldSaveRowsState = isHistoryChangedInPricingSide(
    currentRowsInHistory,
    preSelectedPricingSheetRows,
  );

  /**
   * EFFECTS
   */

  useEffect(() => {
    updateSheetStateLocally();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingSheetRows]);

  usePreventWindowUnload(isUnsavedChanges);

  useEffect(() => {
    setHasUnsavedChanges(isUnsavedChanges);
    updateSheetStateLocally();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnsavedChanges, pricingBasis]);

  useEffect(() => {
    saveRowsState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnsavedChanges, pricingSheetRows]);

  useEffect(() => {
    const interval = setInterval(async () => {
      if (isUnsavedChanges && !error && isOnline) {
        await saveRows(preSelectedPricingSheetRows);
        updatePricingSheetState(
          backgroundInfo.state,
          backgroundInfo.pricingBasis,
        );
        saveRows(preSelectedPricingSheetRows);
      }
    }, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnsavedChanges, error, pricingSheetRows, currentIndex]);

  // change values when component unmount
  useEffect(() => {
    return () => {
      setHasUnsavedChanges(false);
      setItemFilterValue('');
      setSelectedRows([]); // provider
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shouldShowComparisonFilter = isMiraSheet
    ? externalPricingSheetId && miraSuccess && miraSheetId
    : externalPricingSheetId;

  const shouldShowInvalidThresholdFilter = pricingBasis === 'BOTH';

  /**
   * Renderers
   */
  const groupHeaderInnerRenderer = (
    row: any,
    filteredRows: PricingSheetRow[],
  ) => {
    const groupFilter = getGroupFilterForGroup(
      row.node.field,
      row.value,
      groupingStyle,
    );

    const {
      meanFilledCoefficientOfAllDayRows,
      meanFilledCoefficientOfAllDayRowsWeight,
      meanProposalCoefficientOfAllDayRows,
      meanFilledCoefficientOfAllMonthRows,
      meanProposalCoefficientOfAllMonthRows,
      meanFilledCoefficientOfAllMonthRowsWeight,
    } = calculateMeansByGroup({
      rows: filteredRows,
      groupingParams: {
        field: row.node.field,
        value: row.value,
        groupingStyle: groupingStyle,
      },
    });

    const sheetContainsCommentRows =
      filteredRows
        .filter(groupFilter)
        .filter(
          (sheetRow: PricingSheetRow) => sheetRow.approverComment,
        ).length > 0;

    return (
      <>
        <WarningCounts
          countDayPriceWarnings={
            dayWarnings.filter(groupFilter).length
          }
          countMonthPriceWarnings={
            monthWarnings.filter(groupFilter).length
          }
          countDayPriceLightWarnings={
            dayLightWarnings.filter(groupFilter).length
          }
          countMonthPriceLightWarnings={
            monthLightWarnings.filter(groupFilter).length
          }
          countBelow70PercentDayPriceRows={
            dayBelow70Warnings.filter(groupFilter).length
          }
          countBelow70PercentMonthPriceRows={
            monthBelow70Warnings.filter(groupFilter).length
          }
          countZeroDayPriceRows={
            dayZeroPriceWarnings.filter(groupFilter).length
          }
          countZeroMonthPriceRows={
            monthZeroPriceWarnings.filter(groupFilter).length
          }
          countPriceThresholdWarnings={
            invalidPriceThresholdRows.filter(groupFilter).length
          }
          pricingBasis={backgroundInfo.pricingBasis}
        />
        <CoefficientChips
          means={{
            meanFilledDay: meanFilledCoefficientOfAllDayRows,
            meanFilledDayWeight:
              meanFilledCoefficientOfAllDayRowsWeight,
            meanProposalDay: meanProposalCoefficientOfAllDayRows,
            meanFilledMonth: meanFilledCoefficientOfAllMonthRows,
            meanProposalMonth: meanProposalCoefficientOfAllMonthRows,
            meanFilledMonthWeight:
              meanFilledCoefficientOfAllMonthRowsWeight,
          }}
          pricingBasis={backgroundInfo.pricingBasis}
          gridColumnView={gridColumnView}
        />
        {sheetContainsCommentRows && (
          <Box sx={{ ml: 1, mr: 1 }}>
            {showCommentTooltip(
              'Tuoteriville on jätetty kommentti',
              true,
              {
                x: 18,
                y: 12,
              },
            )}
          </Box>
        )}
      </>
    );
  };

  const customHeaderInnerRenderer = (
    filteredRows: PricingSheetRow[],
  ) => {
    const rowsForMeanCalculations = filteredRows
      ? filteredRows?.filter(
          (row: PricingSheetRow) => !row.showByOfferText,
        )
      : [];

    const {
      meanFilledCoefficientOfAllDayRows,
      meanFilledCoefficientOfAllDayRowsWeight,
      meanProposalCoefficientOfAllDayRows,
      meanFilledCoefficientOfAllMonthRows,
      meanProposalCoefficientOfAllMonthRows,
      meanFilledCoefficientOfAllMonthRowsWeight,
    } = calculateMeansByGroup({
      rows: rowsForMeanCalculations,
    });

    const sheetContainsCommentRows =
      filteredRows.filter(
        (sheetRow: PricingSheetRow) => sheetRow.approverComment,
      ).length > 0;
    return (
      <>
        <WarningCounts
          countDayPriceWarnings={dayWarnings.length}
          countMonthPriceWarnings={monthWarnings.length}
          countDayPriceLightWarnings={dayLightWarnings.length}
          countMonthPriceLightWarnings={monthLightWarnings.length}
          countBelow70PercentDayPriceRows={dayBelow70Warnings.length}
          countBelow70PercentMonthPriceRows={
            monthBelow70Warnings.length
          }
          countZeroDayPriceRows={dayZeroPriceWarnings.length}
          countZeroMonthPriceRows={monthZeroPriceWarnings.length}
          countPriceThresholdWarnings={
            invalidPriceThresholdRows.length
          }
          pricingBasis={backgroundInfo.pricingBasis}
        />
        <CoefficientChips
          means={{
            meanFilledDay: meanFilledCoefficientOfAllDayRows,
            meanFilledDayWeight:
              meanFilledCoefficientOfAllDayRowsWeight,
            meanProposalDay: meanProposalCoefficientOfAllDayRows,
            meanFilledMonth: meanFilledCoefficientOfAllMonthRows,
            meanProposalMonth: meanProposalCoefficientOfAllMonthRows,
            meanFilledMonthWeight:
              meanFilledCoefficientOfAllMonthRowsWeight,
          }}
          pricingBasis={backgroundInfo.pricingBasis}
          gridColumnView={gridColumnView}
        />
        {sheetContainsCommentRows && (
          <Box sx={{ ml: 1, mr: 1 }}>
            {showCommentTooltip(
              'Tuoteriville on jätetty kommentti',
              true,
              {
                x: 18,
                y: 12,
              },
            )}
          </Box>
        )}
      </>
    );
  };

  const catClassInnerRenderer = (row: any) => (
    <>
      {row.approverComment && showCommentTooltip(row.approverComment)}
    </>
  );

  const showCommentTooltip = (
    comment: string,
    outlined: boolean = false,
    offsetProps?: { x: number; y: number },
  ) => (
    <CommentToolTip
      offsetProps={offsetProps}
      title={
        <Typography color={theme.palette.text.primary}>
          {comment}
        </Typography>
      }
    >
      <CommentIndicator outlined={outlined} />
    </CommentToolTip>
  );

  const itemCountText = (
    <>
      Hinnoiteltavia tuotteita yhteensä{' '}
      <b>{preSelectedPricingSheetRows.length}</b>, valittu{' '}
      <b>{selectedRows.length}</b>
    </>
  );
  return (
    <>
      {/** FIRST ROW */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          mb: 1,
          [theme.breakpoints.down('md')]: {
            flexDirection: 'column',
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography
            sx={{ ml: 1, mt: 1, mb: 0, fontSize: 26 }}
            variant="h4"
            component="div"
            gutterBottom
            color="primary"
          >
            Hinnoittelu
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              [theme.breakpoints.down('md')]: {
                flexDirection: 'column',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                [theme.breakpoints.down('md')]: {
                  flexDirection: 'column',
                },
              }}
            >
              <Typography
                sx={{ ml: 1, mb: 2 }}
                variant="body2"
                color={'text.secondary'}
              >
                {backgroundInfo?.name
                  ? `${backgroundInfo?.name}`
                  : null}
              </Typography>
              <RentalHistory
                customerId={rentalHistoryProvider.customerId}
                pricingSheetRows={pricingSheetRows}
                dispatchPricingSheetRows={dispatchPricingSheetRows}
                customerName={rentalHistoryProvider.customer}
                startRentalHistoryDate={
                  rentalHistoryProvider.startDate
                }
                endRentalHistoryDate={rentalHistoryProvider.endDate}
                onCustomerChange={handleRentalHistoryCustomerChange}
                companyChains={companyChains}
                rentalHistorySourceType={
                  rentalHistoryProvider.sourceType
                }
                rentalHistoryCompanyName={
                  rentalHistoryProvider.companyName
                }
                isLoadingRentalHistory={
                  isLoadingRentalHistory ||
                  isLoadingRentalHistoryCompanyChain
                }
              />
            </Box>
            {isRenew && (
              <Box sx={{ ml: 4, mr: 4 }}>
                <Alert severity="info" icon={false}>
                  <AlertTitle sx={{ fontWeight: 'bold' }}>
                    Hinnaston voimassaolon jatkaminen
                  </AlertTitle>
                  Olet jatkamassa hinnaston
                  {` ${previousVersionName(name)} `}
                  voimassoloa.
                  <br />
                  Vanhasta hinnastosta on kopiotu tuotteet sekä
                  hinnat, mutta ehdotushinnat ovat saattaneet vaihtua.
                  Uusien ehdotushintojen myötä myös hälytykset
                  saattavat muuttua.
                  <br />
                  <b>Hinnastoon ei voi tehdä muutoksia.</b> Mikäli
                  muutoksille on tarvetta, niin voit tehdä hinnastosta
                  uuden version tai luoda kokonaan uuden hinnaston.
                </Alert>
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                [theme.breakpoints.down('md')]: {
                  flexDirection: 'column',
                },
              }}
            >
              {!isOnline ? (
                <Box
                  sx={{
                    height: 20,
                    [theme.breakpoints.down('md')]: {
                      mb: 2,
                    },
                  }}
                >
                  <NoInternetConnectionAlert />
                </Box>
              ) : null}

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                }}
              >
                <History
                  rowsHistory={rowsHistory}
                  currentIndex={currentIndex}
                  undo={undo}
                  undoToIndex={undoToIndex}
                  redo={redo}
                  redoToIndex={redoToIndex}
                  save={save}
                  tableType={TableType.EditTable}
                />
                <Box sx={{ ml: 2 }}>
                  <Saving
                    saveButton={
                      <Button
                        endIcon={<SaveIcon />}
                        disabled={
                          savingState ||
                          (!isUnsavedChanges && isStateAndSaveSame)
                        }
                        size="small"
                        variant="contained"
                        onClick={onSaveClick}
                        sx={{
                          height: 40,
                          minWidth: 120,
                        }}
                      >
                        {savingState
                          ? 'Tallennetaan...'
                          : !isUnsavedChanges && isStateAndSaveSame
                            ? 'Tallennettu'
                            : 'Tallenna'}
                      </Button>
                    }
                    savingTimeStamp={savingTimeStamp}
                    rowsHistory={rowsHistory}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, mb: 1 }}>
        {backgroundInfo?.approverComment && (
          <CommentPresenter
            header={'Palauttajan perustelut'}
            comment={backgroundInfo.approverComment}
          />
        )}
      </Box>
      {/** SECOND ROW */}
      <Box
        sx={{
          display: 'flex',
          direction: 'row',
          justifyContent: 'flex-start',
          marginTop: 2,
        }}
      >
        <Box sx={{ marginTop: -1 }}>
          <EditPricingBasis
            isDisabled={isEditingDisabled}
            pricingBasis={pricingBasis}
            handlePricingBasisChange={handlePricingBasisChange}
            showOptions={['DAY', 'BOTH']}
          />
        </Box>

        <SearchExternalSheet
          pricingSheetId={pricingSheetId}
          comparisonPricingSheets={comparisonPricingSheets}
          externalPricingSheetId={externalPricingSheetId}
          setExternalPricingSheetId={setExternalPricingSheetId}
          miraSheetIdDefault={miraSheetIdExternal ?? priceGroupId}
          handleClearComparison={handleClearComparisonPrices}
          handleClear={handleClearComparisonPrices}
          tooltipTitle="Lataa hinnasto vertailuun hinnastotyökalusta tai MIRA:sta. Vertailuhinnat latautuvat taulukon VRT-sarakkeisiin"
          selectPlaceholder="Valitse vertailuhinnasto"
          handleExternalItemsResponse={handleExternalItemsResponse}
          handleExternalPricingSheetResponse={
            handleComparisonSheetResponse
          }
          onSelectionChange={handleComparisonSheetChange}
          handlePricingSheetItemsResponse={
            handlePricingSheetItemsResponse
          }
          externalSheetNameDefault={comparisonSheetName}
          selectedOptionDefault={externalSheetSelectedOption}
          setMiraSheetIdExternal={setMiraSheetIdExternal}
          setExternalSheetSelectedOption={
            setExternalSheetSelectedOption
          }
        />

        <Box
          sx={{
            justifySelf: 'flex-end',
            marginLeft: 'auto',
            alignSelf: 'flex-start',
          }}
        >
          {pdfLoading ? (
            <CircularProgress size={20} sx={{ mr: 5 }} />
          ) : (
            <Button
              size="small"
              variant="outlined"
              onClick={async () => onClickDownloadDraftPdf()}
            >
              Lataa luonnos
            </Button>
          )}
        </Box>
      </Box>
      {/** THIRD ROW */}
      <PricingFilters
        addFilter={addFilter}
        pricingBasis={pricingBasis}
        toggleActiveFilter={toggleActiveFilter}
        isChecked={isChecked}
        clearFilters={clearFilters}
        showZeroPriceFilter={true}
        shouldShowComparisonFilter={shouldShowComparisonFilter}
        showPriceThresholdPriceFilter={
          shouldShowInvalidThresholdFilter
        }
      />
      {/** FOURTH ROW */}
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        gap={2}
      >
        <Typography sx={{ mt: 1, mb: 1 }} color="text.primary">
          {itemCountText}
        </Typography>
        <Grid>
          <Grid sx={{ display: 'inline' }}>
            <EditPricesDialog
              isDisabled={isEditingDisabled}
              onChangePrices={handlePriceChanges}
              backgroundInfo={backgroundInfo}
              selectedRows={selectedRows}
            />
          </Grid>
          <Grid sx={{ display: 'inline', ml: 0.5 }}>
            <CopyPricesDialog
              isDisabled={isEditingDisabled}
              enabledSources={['PROPOSAL', 'COMPARISON']}
              onCopyPricesComparison={handleCopyPricesComparison}
              onCopyPricesProposal={handleCopyPricesProposal}
              selectedRows={selectedRows}
              comparisonSheetName={comparisonSheetName}
            />
          </Grid>
          <Grid sx={{ display: 'inline', ml: 0.5 }}>
            <RemoveItemsDialog
              isDisabled={isEditingDisabled}
              selectedRows={selectedRows}
              onRemoveRows={removeRows}
              dialogText={'Hinnoiteltavat tuotteet'}
            />
          </Grid>
          <Grid sx={{ display: 'inline', ml: 0.5 }}>
            <ResetFiltersButton
              gridApi={agGridRef.current?.gridRef?.api}
            />
          </Grid>
        </Grid>
        <Grid justifySelf="flex-end" sx={{ marginLeft: 'auto' }}>
          <Box sx={{ display: 'inline', mr: 0.5 }}>
            <SelectPricingColumns
              handleColumnViewChange={handleColumnViewChange}
              selectableColumns={{
                DAY: AgGridColumnView.PRICING_DAY,
                MONTH: AgGridColumnView.PRICING_MONTH,
                BOTH: AgGridColumnView.PRICING_BOTH,
              }}
              gridColumnView={gridColumnView}
              pricingBasis={pricingBasis}
            />
          </Box>
          <EditViewDialog
            editRoundingBases={
              <EditRoundingBases
                rowsHistory={rowsHistory}
                isUnsavedChanges={isUnsavedChanges}
                updateTimeStampToNewestChange={
                  updateTimeStampToNewestChange
                }
                gridColumnView={gridColumnView}
                updateGridColumnView={updateGridColumnView}
              />
            }
            groupingStyle={groupingStyle}
            groupingValues={groupingValues}
            setDefaultGroupingValues={setDefaultGroupingValues}
            updateGroupingValues={updateGroupingValues}
          />
        </Grid>
      </Grid>
      {/** PRICING TABLE */}
      <AgGrid
        ref={agGridRef}
        pricingBasis={pricingBasis}
        isSelectable={true}
        gridColumnView={gridColumnView}
        type={TableType.EditTable}
        rows={preSelectedRows}
        loading={false}
        columnDefs={columnDefs ?? []}
        externalFilters={activeFilters}
        applyActiveFilters={applyActiveFilters}
        setSelectedRows={setSelectedRows}
        catClassInnerRenderer={catClassInnerRenderer}
        groupHeaderInnerRenderer={groupHeaderInnerRenderer}
        customHeaderInnerRenderer={customHeaderInnerRenderer}
        getMainMenuItems={getMainMenuItems}
      />
    </>
  );
};

export default Pricing;
